import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft, Lock, Feather, FileText } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, ComingSoon } from "@/components/dryvn";
import { CollapsibleList } from "@/components/collapsible-list";
// Screen migration (2026-07-05): letters via hook (titles only — bodies
// stay encrypted, 501-gated until KMS). legacyPrompts stays: static
// guided-question product copy, not user data.
import { legacyPrompts } from "@/data/mock";
import { useLegacyLetters } from "@/lib/queries";
// LANE 2 live-gating (2026-07-10): the letter list is real read-only
// metadata (titles; bodies stay encrypted/masked, same pattern as Vault).
// Only the "coming soon" placeholder badge is demo-only.
import { IS_DEMO } from "@/lib/mode";

export const Route = createFileRoute("/more/legacy")({
  head: () => ({
    meta: [
      { title: "Legacy — Dryvn Life" },
      {
        name: "description",
        content: "Guided questions, letters, and wishes — private and encrypted.",
      },
    ],
  }),
  component: LegacyPage,
});

function LegacyPage() {
  const { data: legacyLetters } = useLegacyLetters();
  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-primary)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Private"
        title={
          <>
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              Legacy
            </em>
            .
          </>
        }
      >
        <p
          className="flex items-center gap-1.5 text-xs"
          style={{ color: "var(--dryvn-primary-deep)" }}
        >
          <Lock className="h-3 w-3" /> Encrypted · private
          {IS_DEMO && (
            <>
              <span className="mx-1 opacity-50">·</span>
              <ComingSoon />
            </>
          )}
        </p>
      </HeroCard>

      <section className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <Feather className="h-4 w-4" style={{ color: "var(--dryvn-primary)" }} />
          <h2 className="font-display text-xl" style={{ color: "var(--dryvn-ink)" }}>
            Guided questions
          </h2>
        </div>
        <div className="space-y-3">
          {legacyPrompts.map((p) => (
            <DCard key={p.id}>
              <p className="font-display italic leading-snug" style={{ color: "var(--dryvn-ink)" }}>
                {p.question}
              </p>
              <p
                className="mt-3 inline-block rounded-lg px-3 py-1.5 font-mono text-xs"
                style={{
                  backgroundColor: "var(--dryvn-page-tint)",
                  color: "var(--dryvn-primary-deep)",
                }}
              >
                Text “{p.smsExample}”
              </p>
            </DCard>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4" style={{ color: "var(--dryvn-primary)" }} />
          <h2 className="font-display text-xl" style={{ color: "var(--dryvn-ink)" }}>
            Saved letters & wishes
          </h2>
        </div>
        <CollapsibleList items={legacyLetters} resource="letters">
          {(shown) => (
            <DCard>
              <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                {shown.map((l) => (
                  <li key={l.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <span
                      className="grid h-9 w-9 place-items-center rounded-xl"
                      style={{ backgroundColor: "var(--dryvn-page-tint)" }}
                    >
                      <Lock className="h-4 w-4" style={{ color: "var(--dryvn-primary-deep)" }} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate text-sm font-medium"
                        style={{ color: "var(--dryvn-ink)" }}
                      >
                        {l.title}
                      </p>
                      <p className="font-mono text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                        {l.date} · •••••••••••
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </DCard>
          )}
        </CollapsibleList>
      </section>
    </AppShell>
  );
}
